import * as focusTrap from "focus-trap";
import { tabbable } from "tabbable";

// Ui.Modal and Ui.Drawer render this element only while open, so connecting is
// opening and disconnecting is closing.

// Shared so focus-trap keeps only the topmost trap active.
const trapStack = [];

const openModals = [];

// The heading Ui.Modal renders its title into. Elm cannot mint its id: two
// open modals each need one of their own.
const TITLE = "[data-modal-title]";

let modalIds = 0;

const MANAGED_TITLE = /^elm-modal-\d+-title$/;

function topmost() {
  return openModals[openModals.length - 1];
}

// Everything off the path from the modal up to the body: a reader's cursor does
// not follow focus, so trapping Tab alone leaves it reading the page behind.
// A modal lower in the stack is spared — inerting it leaves the trap underneath
// with nothing tabbable, which focus-trap throws over.
function siblingsAbove(modal) {
  const siblings = [];
  const holdsOpenModal = (node) => openModals.some((open) => node === open || node.contains(open));

  for (let node = modal; node && node !== document.body; node = node.parentNode) {
    if (!node.parentNode) break;

    for (const sibling of node.parentNode.children) {
      if (sibling === node || sibling.hasAttribute("inert")) continue;
      if (holdsOpenModal(sibling)) continue;

      siblings.push(sibling);
    }
  }

  return siblings;
}

let inerted = [];

// Only the topmost modal hides the page, so closing it restores what it hid.
function isolateTopmost() {
  // The attribute, not the property: `[inert]` is what CSS and queries see.
  for (const element of inerted) element.removeAttribute("inert");

  const modal = topmost();
  inerted = modal ? siblingsAbove(modal) : [];

  for (const element of inerted) element.setAttribute("inert", "");
}

// Escape belongs to the dialog, and only the topmost one answers.
document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape" || event.defaultPrevented) return;

  const modal = topmost();
  if (!modal || modal.dataset.dismissible === "false") return;

  event.preventDefault();
  modal.dispatchEvent(new CustomEvent("close"));
});

// Tab belongs to the dialog too. focus-trap only steps in at the first and
// last tabbable, so anywhere between, the browser picks the next stop. Safari
// skips buttons and links unless "Press Tab to highlight each item" is on, so
// it finds nothing in the dialog and leaves for the address bar. Walking the
// same list focus-trap uses keeps every browser inside. Safari only rings focus
// it moved itself, so every move asks for the ring, which is why focus-trap
// does not wrap on its own.
const TABBABLE = { getShadowRoot: true };

document.addEventListener("keydown", (event) => {
  if (event.key !== "Tab" || event.defaultPrevented) return;

  const modal = topmost();
  if (!modal) return;

  event.preventDefault();

  const stops = tabbable(modal, TABBABLE);
  if (stops.length === 0) return;

  nextStop(stops, focusedElement(), event.shiftKey).focus({ focusVisible: true });
});

// Inside a shadow root, document.activeElement is only the host.
function focusedElement() {
  let element = document.activeElement;
  while (element?.shadowRoot?.activeElement) element = element.shadowRoot.activeElement;
  return element;
}

// By position in the list, which is Tab order: positive tabindex first, then
// the document, shadow roots included. A radio group is one stop, as browsers
// make it: tabbable lists every option of a group with nothing checked, so
// stepping skips the rest of the group the focus is already in.
function nextStop(stops, focused, backward) {
  const index = stops.indexOf(focused);
  if (index === -1) return firstStopFrom(stops, focused, backward);

  const step = backward ? -1 : 1;
  for (let offset = 1; offset < stops.length; offset++) {
    const stop = stops[(((index + step * offset) % stops.length) + stops.length) % stops.length];
    if (!inSameRadioGroup(stop, focused)) return stop;
  }
  return focused;
}

function inSameRadioGroup(node, other) {
  return (
    node.type === "radio" &&
    other.type === "radio" &&
    node.name !== "" &&
    node.name === other.name &&
    node.form === other.form
  );
}

// Focus on something that is not a stop, such as the dialog itself: the next
// stop is the next one after it in the document.
function firstStopFrom(stops, focused, backward) {
  return backward
    ? (stops.findLast((stop) => follows(focused, stop)) ?? stops.at(-1))
    : (stops.find((stop) => follows(stop, focused)) ?? stops[0]);
}

function follows(node, reference) {
  return Boolean(reference.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING);
}

// A click on another <elm-modal> is "outside" this trap, so without allowing it
// each active trap would preventDefault the other modal's controls.
function isInAnyModal(node) {
  return node.closest("elm-modal") != null;
}

// Marks the element whose overflow gets an edge shadow. A drawer has none.
const SCROLLER = "[data-scroll-shadow]";

function markEdges(scroller) {
  const { scrollTop, scrollHeight, clientHeight } = scroller;
  scroller.toggleAttribute("data-more-above", scrollTop > 0);
  scroller.toggleAttribute("data-more-below", scrollHeight - scrollTop - clientHeight > 1);
}

class ElmModal extends HTMLElement {
  connectedCallback() {
    this.returnFocusTo = document.activeElement;
    this.modalId ??= `elm-modal-${++modalIds}`;
    openModals.push(this);
    isolateTopmost();

    // Recorded while we are still in the document: by the time we disconnect,
    // activeElement is the body.
    this.heldFocus = false;
    this.onFocusIn = () => {
      this.heldFocus = true;
    };
    this.addEventListener("focusin", this.onFocusIn);

    // Scroll does not bubble, so one capturing listener covers every scroller.
    this.onScroll = (event) => {
      if (event.target instanceof Element && event.target.matches(SCROLLER)) markEdges(event.target);
    };
    this.addEventListener("scroll", this.onScroll, true);
    this.resizes = new ResizeObserver(() => this.markScrollers());
    // Elm paints the panel a frame later and re-renders it in place after that.
    this.mutations = new MutationObserver(() => {
      this.wire();
      this.watchScrollers();
    });
    this.mutations.observe(this, { childList: true, subtree: true });
    this.wire();

    this.trap = focusTrap.createFocusTrap(this, {
      allowOutsideClick: (event) => isInAnyModal(event.target),
      // Escape closes through Elm, which is what removes this element.
      escapeDeactivates: false,
      // Tab is moved by the keydown handler above, wrap included.
      isKeyForward: () => false,
      isKeyBackward: () => false,
      tabbableOptions: TABBABLE,
      // disconnectedCallback decides where focus goes.
      returnFocusOnDeactivate: false,
      // The dialog, so a reader hears its name before the backdrop's dismiss.
      initialFocus: () => this.dialog() ?? false,
      setReturnFocus: () => false,
      trapStack,
    });

    // Elm paints on an animation frame, so the panel is not here yet.
    this.activation = requestAnimationFrame(() => this.activate());
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.activation);
    this.removeEventListener("focusin", this.onFocusIn);
    this.removeEventListener("scroll", this.onScroll, true);
    this.mutations.disconnect();
    this.resizes.disconnect();

    // Before deactivate: it hands control to the trap underneath, which has to
    // be reachable by then.
    const index = openModals.indexOf(this);
    if (index !== -1) openModals.splice(index, 1);

    isolateTopmost();

    this.trap?.deactivate();
    this.trap = undefined;

    // Not while another modal still holds focus.
    if (this.heldFocus && !topmost() && this.returnFocusTo?.isConnected) {
      this.returnFocusTo.focus();
    }

    this.returnFocusTo = undefined;
    this.heldFocus = false;
  }

  activate() {
    const dialog = this.dialog();
    if (dialog) dialog.tabIndex = -1;

    this.watchScrollers();
    this.trap?.activate();
  }

  // Content growing changes scrollHeight without resizing the scroller, so
  // its children are watched too.
  watchScrollers() {
    this.resizes.disconnect();
    for (const scroller of this.own(SCROLLER)) {
      this.resizes.observe(scroller);
      for (const child of scroller.children) this.resizes.observe(child);
    }
    this.markScrollers();
  }

  markScrollers() {
    for (const scroller of this.own(SCROLLER)) markEdges(scroller);
  }

  wire() {
    const dialog = this.dialog();
    if (!dialog) return;

    const [title] = this.own(TITLE);
    if (!title) {
      // One we did not mint is the caller's to keep.
      if (MANAGED_TITLE.test(dialog.getAttribute("aria-labelledby") ?? "")) {
        dialog.removeAttribute("aria-labelledby");
      }
      return;
    }

    title.id ||= `${this.modalId}-title`;
    dialog.setAttribute("aria-labelledby", title.id);
  }

  // Ours, not a nested modal's.
  own(selector) {
    return [...this.querySelectorAll(selector)].filter((el) => el.closest("elm-modal") === this);
  }

  dialog() {
    return this.own('[role="dialog"]')[0] ?? null;
  }
}

if (!customElements.get("elm-modal")) {
  customElements.define("elm-modal", ElmModal);
}
